'use client';

import { isClient } from '@/lib/utils';
import {
  Analytics,
  initializeAnalytics,
  isSupported,
  logEvent as logEventFirebase,
} from 'firebase/analytics';
import { FirebaseOptions, initializeApp } from 'firebase/app';
import { createContext, ReactNode, useContext } from 'react';

const FirebaseTrackingContext = createContext<{
  logEvent: (
    eventName: string,
    options?: Record<string, string | undefined | string[]>,
  ) => void;
  logScreenView: (
    eventName: string,
    options?: Record<string, string | undefined | string[]>,
  ) => void;
}>({
  logEvent: () => {},
  logScreenView: () => {},
});

const firebaseAnalyticsConfiguration = {
  config: {
    send_page_view: false,
  },
};

let analytics: Analytics;

export const FirebaseTrackingProvider = ({
  children,
  config,
}: {
  children: ReactNode;
  config: FirebaseOptions;
}) => {
  const isFirebaseActive = config.apiKey && isClient;

  if (!isFirebaseActive) {
    return <>{children}</>;
  }

  const app = initializeApp(config);

  void isSupported().then((isSupported) => {
    if (!isSupported) {
      return;
    }

    analytics = initializeAnalytics(app, firebaseAnalyticsConfiguration);
  });

  const logEvent = (
    eventName: string,
    options?: Record<string, string | undefined | string[]>,
  ) => {
    if (analytics) {
      logEventFirebase(analytics, eventName, options);
    }
  };

  const logScreenView = (
    screenName: string,
    options?: Record<string, string | undefined | string[]>,
  ) => {
    logEvent('screen_view', { firebase_screen: screenName, ...options });
  };

  return (
    <FirebaseTrackingContext.Provider
      value={{
        logEvent,
        logScreenView,
      }}
    >
      {children}
    </FirebaseTrackingContext.Provider>
  );
};

export const useFirebaseTracking = () => {
  const context = useContext(FirebaseTrackingContext);
  if (context === undefined) {
    throw new Error(
      'useFirebaseTracking must be used within a FirebaseTrackingProvider',
    );
  }
  return context;
};
